import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { emailSchema, passwordSchema } from '@tailoring-crm/shared';
import { AuthLayout } from '../components/auth/auth-layout.js';
import { FormError, PasswordField, EmailField } from '../components/auth/auth-fields.js';
import { OtpStep } from '../components/auth/otp-step.js';
import { Button, useToast } from '@tailoring-crm/ui';
import { useApiClient } from '../lib/api/api-context.js';
import {
  completePasswordReset,
  startPasswordReset,
  verifyPasswordReset,
} from '../lib/auth/auth-api.js';
import type { OtpChallenge } from '../lib/auth/auth-contract.js';
import { authErrorMessage } from '../lib/auth/auth-errors.js';

/**
 * Forgotten password, recovered by email.
 *
 * Note what the first step does NOT say: whether that address has an account. The API answers
 * identically either way — it is the same "we have sent a code" screen — because a reset form
 * that says "no account with that address" is a directory of which email addresses belong to
 * shop owners, free to anyone with a script.
 *
 * Completing a reset ends every session everywhere, which is the whole point of the flow: the
 * common reason for using it is that somebody else may have the old password.
 */
type Step = 'email' | 'code' | 'password';

export function ForgotPasswordPage(): ReactElement {
  const { t } = useTranslation();
  const client = useApiClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [verificationToken, setVerificationToken] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);

  const request = async (address: string): Promise<OtpChallenge> =>
    await startPasswordReset(client, address);

  const submitEmail = (): void => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailError(parsed.error.issues[0]?.message);
      return;
    }
    setEmailError(undefined);
    setEmail(parsed.data);
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        setChallenge(await request(parsed.data));
        setStep('code');
      } catch (caught) {
        setError(authErrorMessage(caught, t));
      } finally {
        setBusy(false);
      }
    })();
  };

  const submitCode = (code: string): void => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const verified = await verifyPasswordReset(client, { email, code });
        setVerificationToken(verified.verificationToken);
        setStep('password');
      } catch (caught) {
        setError(authErrorMessage(caught, t));
      } finally {
        setBusy(false);
      }
    })();
  };

  const resend = (): void => {
    setResending(true);
    void (async () => {
      try {
        setChallenge(await request(email));
        setError(null);
      } catch (caught) {
        setError(authErrorMessage(caught, t));
      } finally {
        setResending(false);
      }
    })();
  };

  const submitPassword = (): void => {
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setPasswordError(parsed.error.issues[0]?.message);
      return;
    }
    setPasswordError(undefined);
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await completePasswordReset(client, { verificationToken, password });
        toast({ title: t('auth.passwordResetDone'), variant: 'success' });
        // Straight to the login screen, not into the app: the reset deliberately revoked every
        // session, including any this browser was holding.
        void navigate('/login', { replace: true });
      } catch (caught) {
        setError(authErrorMessage(caught, t));
        setBusy(false);
      }
    })();
  };

  const footer = (
    <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
      {t('auth.backToSignIn')}
    </Link>
  );

  if (step === 'code' && challenge) {
    return (
      <AuthLayout
        title={t('auth.verifyEmailTitle')}
        description={t('auth.resetVerifyDescription')}
        footer={footer}
      >
        <OtpStep
          challenge={challenge}
          submitting={busy}
          resending={resending}
          error={error}
          onSubmit={submitCode}
          onResend={resend}
          onChangeNumber={() => {
            setStep('email');
            setError(null);
          }}
        />
      </AuthLayout>
    );
  }

  if (step === 'password') {
    return (
      <AuthLayout
        title={t('auth.newPasswordTitle')}
        description={t('auth.newPasswordDescription')}
        footer={footer}
      >
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            submitPassword();
          }}
          noValidate
        >
          <FormError message={error} />
          <PasswordField
            label={t('auth.newPassword')}
            hint={t('auth.passwordHint')}
            error={passwordError}
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <Button type="submit" block disabled={busy}>
            {t('auth.setNewPassword')}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.forgotTitle')}
      description={t('auth.forgotDescription')}
      footer={footer}
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          submitEmail();
        }}
        noValidate
      >
        <FormError message={error} />
        <EmailField
          label={t('auth.emailAddress')}
          error={emailError}
          value={email}
          autoFocus
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button type="submit" block disabled={busy}>
          {t('auth.sendCode')}
        </Button>
      </form>
    </AuthLayout>
  );
}

export default ForgotPasswordPage;
